/**
 * @fileoverview Determines which elevation service to use based on the active map provider.
 *
 * **Why we do this:** Floor-height estimation needs ground elevation in the same vertical
 * datum as caller altitude. Our call/location data returns altitude in HAE (height above
 * WGS84 ellipsoid). So we must always work in HAE: (caller HAE − ground HAE) = height
 * above ground → floor level.
 *
 * - **Google map**: Prefer Google Elevation API + geoid conversion (MSL → HAE).
 *   If conversion fails, try ArcGIS HAE. Raw Google MSL fallback is opt-in
 *   because mixed datums can significantly bias floor estimates.
 * - **ESRI map**: We prefer the ArcGIS Elevation API, which can return HAE directly. If
 *   ArcGIS fails (e.g. missing API permission), we fall back to Google + geoid conversion.
 *
 * This module routes by map type so each map uses its natural provider first, with a
 * single, consistent HAE result for the rest of the app.
 */
import { getGroundElevationArcGIS } from './arcgisElevationService';
import { mslToHae } from './geoidService';
import { getGroundElevation as getGroundElevationGoogle } from './googleElevationService';

export type MapElevationProvider = 'google' | 'esri';

export type GetGroundElevation = (
  latitude: number,
  longitude: number,
) => Promise<number | null>;

const elevationCache = new Map<string, number>();
const ALLOW_RAW_GOOGLE_MSL_FALLBACK =
  typeof process !== 'undefined' &&
  process.env?.ALLOW_PURE_WIRELESS_RAW_MSL_FALLBACK === 'true';

function cacheKey(lat: number, lng: number): string {
  return `${lat.toFixed(6)},${lng.toFixed(6)}`;
}

/**
 * Fetches ground elevation via Google (MSL) and converts to HAE.
 *
 * **Why this is needed:** The Google Elevation API returns height above mean sea level
 * (MSL/orthometric), not HAE. Our call data returns altitude in HAE. If we used Google’s
 * MSL value as-is, (caller HAE − ground MSL)
 * would mix datums and give wrong height-above-ground. So we convert Google’s MSL to
 * HAE using the EGM96 geoid undulation (N): HAE = MSL + N.
 * If geoid lookup fails, return null so callers can try another HAE source
 * (e.g. ArcGIS) instead of mixing datums. As a last resort, callers may still
 * opt to use raw MSL to avoid losing floor estimation entirely.
 *
 * @param latitude - Latitude in degrees
 * @param longitude - Longitude in degrees
 * @returns Ground elevation in meters (HAE), or null if the request fails
 */
async function getHaeViaGoogle(
  latitude: number,
  longitude: number,
): Promise<number | null> {
  const msl = await getGroundElevationGoogle(latitude, longitude);
  if (msl === null) return null;

  const hae = await mslToHae(latitude, longitude, msl);
  if (hae !== null) return hae;

  return null;
}

/**
 * Returns the elevation getter to use for the given map provider.
 *
 * - **google**: Google (MSL) + geoid → HAE, then ArcGIS HAE fallback.
 *   Raw Google MSL fallback is disabled by default.
 * - **esri**: ArcGIS (HAE), with fallback to Google + geoid.
 */
export function whichElevationService(
  mapProvider: MapElevationProvider,
): GetGroundElevation {
  return async function getGroundElevation(
    latitude: number,
    longitude: number,
  ): Promise<number | null> {
    const key = cacheKey(latitude, longitude);
    if (elevationCache.has(key)) {
      return elevationCache.get(key) ?? null;
    }

    let elevation: number | null = null;

    if (mapProvider === 'google') {
      elevation = await getHaeViaGoogle(latitude, longitude);
      if (elevation === null) {
        elevation = await getGroundElevationArcGIS(latitude, longitude);
      }
      if (elevation === null) {
        if (ALLOW_RAW_GOOGLE_MSL_FALLBACK) {
          // Optional escape hatch for environments that explicitly prioritize
          // availability over vertical-datum precision.
          elevation = await getGroundElevationGoogle(latitude, longitude);
        }
      }
    } else {
      elevation = await getGroundElevationArcGIS(latitude, longitude);
      if (elevation === null) {
        elevation = await getHaeViaGoogle(latitude, longitude);
      }
    }

    if (elevation !== null) {
      elevationCache.set(key, elevation);
    }

    return elevation;
  };
}
